import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { api, JOB_KINDS, JOB_TERMINAL, type Job, type JobKind, type JobLog } from '../lib/api';
import { errorMessage, short, when } from '../lib/format';

/** D1/D2 jobs: long-running work in the workspace VM, billed per minute of VM time while it runs. */
export function JobsPage() {
  const params = useParams({ strict: false }) as { jobId?: string };
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: api.jobs, refetchInterval: 10_000 });
  return (
    <div className="chat">
      <aside className="threads stack">
        <Link to="/jobs" className="primary" style={{ textAlign: 'center' }}>
          + New job
        </Link>
        {jobs.data?.jobs.map((j) => (
          <Link
            key={j.id}
            to="/jobs/$jobId"
            params={{ jobId: j.id }}
            className={j.id === params.jobId ? 'active' : ''}
          >
            <div className="row">
              <StatusPill status={j.status} />
              <span className="muted" style={{ fontSize: 12 }}>
                {j.kind}
              </span>
            </div>
            <div>
              <code>{short(j.spec.command, 36)}</code>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {when(j.createdAt)}
            </div>
          </Link>
        ))}
      </aside>
      {params.jobId ? <JobView key={params.jobId} id={params.jobId} /> : <NewJob />}
    </div>
  );
}

function StatusPill({ status }: { status: Job['status'] }) {
  const cls =
    status === 'running' || status === 'queued'
      ? 'pill busy'
      : status === 'succeeded'
        ? 'pill live'
        : 'pill danger';
  return <span className={cls}>{status}</span>;
}

function NewJob() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [command, setCommand] = useState('');
  const [kind, setKind] = useState<JobKind>('shell');
  const [timeoutMin, setTimeoutMin] = useState('60');
  const start = useMutation({
    mutationFn: () =>
      api.startJob({
        kind,
        command: command.trim(),
        timeoutSec: Math.max(1, Number(timeoutMin)) * 60,
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['jobs'] });
      await nav({ to: '/jobs/$jobId', params: { jobId: r.id } });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    start.mutate();
  };
  return (
    <div className="conversation">
      <div className="conv-head">
        <span className="muted">New job</span>
      </div>
      <div className="messages">
        <form onSubmit={submit} className="panel stack" style={{ maxWidth: 720 }}>
          <h2 style={{ margin: 0 }}>Run something in your workspace</h2>
          <p className="muted">
            Jobs run in the background in your VM (bash, Python quant venv at /opt/tah/venv). Output
            is captured here. The VM is billed per minute while a job runs and stops billing when it
            ends.
          </p>
          <label className="field">
            <span>Command (bash -lc)</span>
            <textarea
              rows={4}
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              placeholder="/opt/tah/venv/bin/python backtest.py --symbol ETH --days 90"
            />
          </label>
          <div className="row wrap">
            <label className="field" style={{ width: 180 }}>
              <span>Kind</span>
              <select value={kind} onChange={(e) => setKind(e.target.value as JobKind)}>
                {JOB_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <label className="field" style={{ width: 180 }}>
              <span>Timeout (minutes)</span>
              <input
                inputMode="numeric"
                value={timeoutMin}
                onChange={(e) => setTimeoutMin(e.target.value)}
              />
            </label>
          </div>
          <div className="row">
            <button type="submit" className="primary" disabled={start.isPending || !command.trim()}>
              {start.isPending ? 'Starting…' : 'Start job'}
            </button>
            {start.error && <span className="error">{errorMessage(start.error)}</span>}
          </div>
        </form>
      </div>
    </div>
  );
}

/** Live view: initial REST load, then SSE for log chunks + state until the job is terminal. */
function JobView({ id }: { id: string }) {
  const qc = useQueryClient();
  const initial = useQuery({ queryKey: ['job', id], queryFn: () => api.job(id) });
  const [job, setJob] = useState<Job | null>(null);
  const [logs, setLogs] = useState<JobLog[]>([]);
  const lastSeq = useRef(0);
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initial.data) setJob(initial.data.job);
  }, [initial.data]);

  useEffect(() => {
    let es: EventSource | null = null;
    let cancelled = false;
    (async () => {
      const first = await api.jobLogs(id, 0);
      if (cancelled) return;
      setLogs(first.entries);
      lastSeq.current = first.next;
      es = new EventSource(`/v1/jobs/${id}/events?after=${lastSeq.current}`);
      es.addEventListener('log', (e) => {
        const l = JSON.parse((e as MessageEvent).data) as JobLog;
        if (l.seq <= lastSeq.current) return;
        lastSeq.current = l.seq;
        setLogs((prev) => [...prev, l]);
      });
      es.addEventListener('job', (e) => {
        const j = JSON.parse((e as MessageEvent).data) as Job | null;
        if (j) setJob(j);
        if (j && JOB_TERMINAL.has(j.status)) qc.invalidateQueries({ queryKey: ['jobs'] });
      });
      // The server keeps the stream open for one more pass after the terminal state so late log chunks
      // arrive, then sends `end`. Closing on the terminal `job` event used to drop those chunks.
      es.addEventListener('end', () => {
        es?.close();
        void api.jobLogs(id, lastSeq.current).then((more) => {
          if (cancelled || more.entries.length === 0) return;
          lastSeq.current = more.next;
          setLogs((prev) => [...prev, ...more.entries]);
        });
      });
    })().catch(() => {});
    return () => {
      cancelled = true;
      es?.close();
    };
  }, [id, qc]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when new output arrives
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [logs.length]);

  const kill = useMutation({
    mutationFn: () => api.killJob(id),
    onSuccess: (r) => {
      setJob(r.job);
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
  const active = job && !JOB_TERMINAL.has(job.status);
  return (
    <div className="conversation">
      <div className="conv-head">
        {job && <StatusPill status={job.status} />}
        <code>{job ? short(job.spec.command, 70) : id}</code>
        <span className="grow" />
        {job?.exitCode !== null && job?.exitCode !== undefined && (
          <span className="muted">exit {job.exitCode}</span>
        )}
        {job?.startedAt && <span className="muted">started {when(job.startedAt)}</span>}
        {active && (
          <button
            type="button"
            className="small danger"
            disabled={kill.isPending}
            onClick={() => kill.mutate()}
          >
            Kill
          </button>
        )}
      </div>
      <div className="messages">
        {initial.error && <span className="error">{errorMessage(initial.error)}</span>}
        {job?.note && <div className="warn">{job.note}</div>}
        {job?.spec.origin?.threadId && (
          <div className="muted">
            Started by the agent in{' '}
            <Link to="/chat/$threadId" params={{ threadId: job.spec.origin.threadId }}>
              this thread
            </Link>
            .
          </div>
        )}
        <pre style={{ maxHeight: 'none', flex: 1 }}>
          {logs.length === 0 && <span className="muted">(no output yet)</span>}
          {logs.map((l) => (
            <span key={l.seq} className={l.stream === 'stderr' ? 'warn' : ''}>
              {l.chunk}
            </span>
          ))}
        </pre>
        <div ref={bottom} />
      </div>
      <div className="composer muted">
        {active
          ? 'Running — output streams live.'
          : `Ended ${job?.endedAt ? when(job.endedAt) : ''}.`}
      </div>
    </div>
  );
}
